import { Injectable, inject } from '@angular/core';
import {
  EventTypes,
  OidcSecurityService,
  PublicEventsService,
} from 'angular-auth-oidc-client';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, filter, firstValueFrom } from 'rxjs';
import createDebug from 'debug';
import { User } from '../../business-objects/user-form-group';

const Debug = createDebug('AuthService');

/**
 * Auth Service
 *
 * Client-side authentication state and actions. Fetches the auth config
 * and current user on load and provides role checking, login, and logout.
 *
 * Consumers can access auth state two ways:
 * - **Subscription**: `authService.currentUser` emits whenever the current user changes
 * - **Async**: `await authService.getCurrentUser()` resolves with the user once initialization completes
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  /** Is Authenticated - Whether the user is currently authenticated (undefined until checked) */
  private _authenticated = new BehaviorSubject<boolean | undefined>(undefined);

  /** Is Authenticated - Observable that emits the authentication state on each change */
  authenticated: Observable<boolean | undefined> =
    this._authenticated.asObservable();

  /** Current User - BehaviorSubject holding the authenticated user */
  private _currentUser = new BehaviorSubject<User | undefined>(undefined);

  /** Current User - Observable that emits the current user on each change */
  currentUser: Observable<User | undefined> = this._currentUser.asObservable();

  /** Auth Enabled - Whether OIDC auth is configured (undefined until config loads) */
  private _authEnabled = new BehaviorSubject<boolean | undefined>(undefined);

  /** Auth Enabled - Observable that emits whether auth is configured on each change */
  authEnabled: Observable<boolean | undefined> =
    this._authEnabled.asObservable();

  private oidcSecurityService = inject(OidcSecurityService);

  private publicEventsService = inject(PublicEventsService);

  private router = inject(Router);

  /** Return URL - URL to navigate to after login */
  returnUrl?: string;

  /** Login Path - Path to navigate to when not authenticated (defaults to /login) */
  private _loginPath = '/login';

  /** Auth Config - Cached client-facing auth config from the server */
  private _authConfig: Record<string, any> = {};

  /** Rejected - Whether the current user was rejected by the server (not registered) */
  private _rejected = false;

  /** Ready Resolve - Resolves the ready promise when initialization completes */
  private readyResolve!: () => void;

  /** Ready Promise - Promise that resolves once initialization completes */
  private readyPromise = new Promise<void>((resolve) => {
    this.readyResolve = resolve;
  });
  constructor() {
    this.initialize();
  }

  /** Initialize - Checks OIDC state and fetches current user if authenticated */

  async initialize(): Promise<void> {
    const debug = Debug.extend('initialize');
    debug('initializing');

    // Resolved before checkAuth so a checkAuth *failure* can never be mistaken
    // for "auth is not configured" in the catch below.
    let hasConfig = false;

    try {
      // Check if the OIDC provider loaded a real config (has an authority)
      const config = await firstValueFrom(
        this.oidcSecurityService.getConfiguration(),
      );
      debug('config', config);
      // provide-auth feeds a stub OpenIdConfiguration to angular-auth-oidc-client
      // when the server reports auth is disabled (its validator rejects `{}`).
      // The stub is tagged with clientId `auth-disabled` so we can still
      // recognize the "no real config" state here.
      hasConfig = !!config?.authority && config.clientId !== 'auth-disabled';
      this._authEnabled.next(hasConfig);

      const authResult = await firstValueFrom(
        this.oidcSecurityService.checkAuth(),
      );
      debug('authResult', authResult);

      // Fetch and cache the server auth config (loginPath, allowImpersonation, etc.)
      try {
        // Relative to <base href> so a configured basePath (e.g.
        // /apexDesigner) is honored — a leading-slash URL would bypass it.
        const authConfigResponse = await fetch('api/auth/config');
        if (authConfigResponse.ok) {
          this._authConfig = await authConfigResponse.json();
          debug('authConfig', this._authConfig);
          if (this._authConfig.loginPath) {
            this._loginPath = this._authConfig.loginPath;
          }
        }
      } catch {}

      if (hasConfig && authResult?.isAuthenticated) {
        this._authenticated.next(true);

        let user: User | undefined;
        try {
          user = (await User.currentUser()) ?? undefined;
        } catch (err: any) {
          if (err?.status === 403) {
            debug('user not authorized, redirecting');
            this._rejected = true;
            this._authenticated.next(false);
            this.router.navigate(['/not-authorized'], {
              queryParams: { email: err.error?.email },
            });
            this.readyResolve();
            return;
          }
          throw err;
        }
        debug('user', user);

        if (user) {
          this._currentUser.next(user);
        }

        this.watchForSessionEnd();

        const returnUrl = sessionStorage.getItem('auth_return_url');
        if (returnUrl) {
          sessionStorage.removeItem('auth_return_url');
          debug('navigating to returnUrl', returnUrl);
          this.router.navigateByUrl(returnUrl);
        }
      } else {
        this._authenticated.next(false);
        // Do not redirect here — the roleGuard handles redirecting
        // unauthenticated users on protected routes. Redirecting here
        // would break public routes (landing pages, etc.) that don't
        // require authentication.
        debug('not authenticated');
        // When the server has auth disabled, the auth middleware can still
        // resolve a user (via impersonation header). Pull it in so role
        // guards have a user to evaluate against.
        if (!hasConfig) {
          try {
            const user = (await User.currentUser()) ?? undefined;
            if (user) {
              this._currentUser.next(user);
            }
          } catch {}
        }
      }
    } catch (error) {
      debug('error', error);

      // A failure here means "auth is enabled and this visitor is not signed
      // in" — checkAuth rejects on ordinary conditions such as a stale `state`
      // left in the URL by an abandoned callback. Reporting auth as disabled
      // would tell the role guard to wave anonymous visitors onto protected
      // routes, so keep whatever the OIDC config said.
      this._authEnabled.next(hasConfig);
      this._authenticated.next(false);
      this._currentUser.next(undefined);
      // OIDC may throw when no real auth config is loaded (dev/e2e with
      // AUTH_DISABLED). The server can still resolve a user via the
      // impersonation header — fetch it so role guards can evaluate.
      if (!hasConfig) {
        try {
          const user = (await User.currentUser()) ?? undefined;
          if (user) {
            this._currentUser.next(user);
          }
        } catch {}
      }
    }
    this.readyResolve();
  }

  /**
   * Watch For Session End - Signs the user out on the client when the OIDC
   * session ends after load.
   *
   * When a refresh fails, angular-auth-oidc-client clears the stored tokens,
   * stops its periodic check and emits `isAuthenticated: false`. Without this,
   * the current user stayed set, the role guard let navigation through, and
   * API calls went out with no token and were refused, leaving the page
   * waiting. `TokenExpired` and `IdTokenExpired` are not used: they fire
   * shortly before expiry to trigger a normal renew.
   */
  private watchForSessionEnd(): void {
    this.oidcSecurityService.isAuthenticated$
      .pipe(filter((result) => !result.isAuthenticated))
      .subscribe(() => this.endSession('isAuthenticated false'));

    this.publicEventsService
      .registerForEvents()
      .pipe(filter((event) => event.type === EventTypes.SilentRenewFailed))
      .subscribe(() => this.endSession('silent renew failed'));
  }

  /** End Session - Clears the signed-in state and sends the user to the login path */
  private endSession(reason: string): void {
    const debug = Debug.extend('endSession');
    // Only the first signal counts; a failed renew raises several
    if (!this._authenticated.value) return;
    debug('session ended: %s', reason);

    this._authenticated.next(false);
    this._currentUser.next(undefined);

    // Return to the current page after signing in again
    const url = this.router.url;
    if (!url.startsWith(this._loginPath)) {
      this.returnUrl = url;
      this.router.navigate([this._loginPath]);
    }
  }

  /** Get Rejected - Returns whether the user was rejected by the server */
  async getRejected(): Promise<boolean> {
    await this.readyPromise;
    return this._rejected;
  }

  /** Get Auth Enabled - Returns whether auth is configured once initialization completes */
  async getAuthEnabled(): Promise<boolean> {
    await this.readyPromise;
    return this._authEnabled.value ?? false;
  }

  /** Get Is Authenticated - Returns whether the user is authenticated once initialization completes */
  async getAuthenticated(): Promise<boolean> {
    await this.readyPromise;
    return this._authenticated.value ?? false;
  }

  /** Get Current User - Returns the current user once initialization completes */
  async getCurrentUser(): Promise<User | undefined> {
    const debug = Debug.extend('getCurrentUser');
    debug('waiting for ready');

    await this.readyPromise;
    debug('currentUser', this._currentUser.value);

    return this._currentUser.value;
  }

  /** Has Role - Checks if the current user has a specific role */
  async hasRole(roleName: string): Promise<boolean> {
    const debug = Debug.extend('hasRole');
    debug('roleName', roleName);

    await this.readyPromise;

    const user = this._currentUser.value;
    const result =
      user?.roleAssignments?.some((ra: any) => ra.role?.name === roleName) ??
      false;
    debug('result', result);

    return result;
  }

  /** Access Token - Observable that emits the current OIDC access token */
  accessToken: Observable<string> = this.oidcSecurityService.getAccessToken();

  /** Get Access Token - Returns the current OIDC access token */
  async getAccessToken(): Promise<string> {
    return firstValueFrom(this.oidcSecurityService.getAccessToken());
  }

  /** Get Login Path - Returns the configured redirect path for unauthenticated users */
  getLoginPath(): string {
    return this._loginPath;
  }

  /** Get Auth Config - Returns the cached client-facing auth config from the server */
  async getAuthConfig(): Promise<Record<string, any>> {
    await this.readyPromise;
    return this._authConfig;
  }

  /** Login - Saves return URL and initiates the OIDC login flow */
  login(returnUrl?: string): void {
    const debug = Debug.extend('login');
    debug('login, returnUrl %s', returnUrl);
    if (returnUrl) {
      this.returnUrl = returnUrl;
    }
    if (this.returnUrl) {
      sessionStorage.setItem('auth_return_url', this.returnUrl);
      debug('saved returnUrl', this.returnUrl);
    }
    this.oidcSecurityService.authorize();
  }

  /** Logout - Clears any impersonated identity, then ends the OIDC session */
  logout(): void {
    const debug = Debug.extend('logout');
    debug('logout');
    // sessionStorage survives the logout redirect, so a leftover key would
    // silently resume impersonation on the next login in this tab.
    sessionStorage.removeItem('impersonateEmail');
    if (this._authenticated.value) {
      this.oidcSecurityService.logoffAndRevokeTokens().subscribe();
    } else {
      // No OIDC session to end (disabled-auth mode) — reload so the app
      // re-initializes without a resolved user.
      window.location.reload();
    }
  }
}
